import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { API_BASE } from '@/lib/api-base';

export interface StorefrontBrand {
  tenantSlug: string;
  brandName: string;
  brandShortName: string;
  legalName: string;
  logoUrl?: string;
  portalThemeTemplate?: string;
  tagline: string;
  primaryEmail: string;
  financeEmail: string;
  supportEmail: string;
  primaryPhone: string;
  secondaryPhone: string;
  addressLines: string[];
  officeHours: string[];
  footerBlurb: string;
  portalLabel: string;
}

export interface StorefrontVisualProfile {
  industryTemplate: string;
  tone: string;
  mediaStrategy: string;
  heroMediaUrl: string;
  heroMediaAlt: string;
  visualPattern: string;
  palette: string;
}

export interface StorefrontRoute {
  routePath: string;
  routeType: 'page' | 'form' | 'module' | 'redirect';
  targetKey: string;
  authMode: 'public' | 'optional' | 'required';
  configJson: Record<string, unknown>;
}

export interface StorefrontFormField {
  name: string;
  label?: string;
  type: string;
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

export interface StorefrontFormDefinition {
  formKey: string;
  title: string;
  description: string | null;
  entityType: string;
  schemaJson: {
    fields?: StorefrontFormField[];
  } & Record<string, unknown>;
  submitConfig: Record<string, unknown>;
  notificationCfg: Record<string, unknown>;
}

export interface StorefrontModuleDefinition {
  moduleKey: string;
  moduleType: string;
  configJson: Record<string, unknown>;
}

export interface StorefrontContentPage {
  pageKey: string;
  path?: string;
  title?: string;
  intro?: string;
  sections?: Array<Record<string, unknown>>;
}

type StorefrontExperienceMode = 'storefront' | 'portal';

interface StorefrontContextValue {
  isLoading: boolean;
  host: string;
  tenantSlug: string;
  siteKey: string;
  experienceMode: StorefrontExperienceMode;
  storefrontBaseUrl: string | null;
  portalBaseUrl: string | null;
  loginUrl: string;
  portalHomeUrl: string;
  tokenKey: string;
  legacyTokenKeys: string[];
  brand: StorefrontBrand;
  visual: StorefrontVisualProfile;
  home: Record<string, unknown>;
  pages: StorefrontContentPage[];
  routes: StorefrontRoute[];
  forms: StorefrontFormDefinition[];
  modules: StorefrontModuleDefinition[];
  featureFlags: Record<string, boolean>;
  raw: Record<string, unknown> | null;
}

const DEFAULT_BRAND: StorefrontBrand = {
  tenantSlug: 'ge-lines',
  brandName: 'GE Lines Pte Ltd',
  brandShortName: 'GE Lines',
  legalName: 'GE Lines Pte Ltd',
  logoUrl: undefined,
  portalThemeTemplate: 'freight_forwarder',
  tagline: 'Global Logistics. Local Expertise.',
  primaryEmail: 'operations@gelines.com.sg',
  financeEmail: 'finance@gelines.com.sg',
  supportEmail: 'gerard.sg@gelines.com',
  primaryPhone: '+65 6221 0711',
  secondaryPhone: '+65 6221 1201',
  addressLines: ['100D Pasir Panjang Road', 'The Meissa #02-01, Singapore 118520'],
  officeHours: ['Mon – Fri: 8:30 AM – 6:00 PM', 'Sat: 8:30 AM – 1:00 PM'],
  footerBlurb: 'Singapore NVOCC & freight forwarding since 2006. Connecting Asia to the world across 6 countries.',
  portalLabel: 'Customer Portal',
};

const DEFAULT_HOME = {
  heroBadge: 'Singapore NVOCC & Freight Forwarder · Est. 2006',
  heroTitle: 'Global Logistics.',
  heroAccent: 'Local Expertise.',
  heroDescription: 'Empowering global freight through Singapore\'s transshipment hub. Sea freight, air cargo, and door-to-door delivery since 2006.',
  stats: [
    { value: '22K+', label: 'Happy Customers' },
    { value: '30+', label: 'Team Members' },
    { value: '6', label: 'Countries' },
    { value: '2006', label: 'Est.' },
  ],
  services: [
    { title: 'Sea Freight', desc: 'FCL, LCL and NVOCC services for regional and global shipping.', icon: 'ship', color: 'brand' },
    { title: 'Air Freight', desc: 'Time-sensitive cargo solutions via Changi and global partner network.', icon: 'plane', color: 'ocean' },
    { title: 'Transportation', desc: 'Door-to-door transport and inland coordination for import and export.', icon: 'truck', color: 'emerald' },
    { title: 'Custom Documentation', desc: 'Customs clearance, permit handling and compliance support.', icon: 'file-check', color: 'amber' },
  ],
  networkTitle: 'Serving 6 Countries Across Asia',
  networkSubtitle: 'Established in Singapore, serving the world',
  networkCountries: [
    { flag: 'SG', country: 'Singapore', note: 'Headquarters' },
    { flag: 'PH', country: 'Philippines', note: 'Manila Office' },
    { flag: 'TH', country: 'Thailand', note: 'Bangkok Office' },
    { flag: 'BN', country: 'Brunei', note: 'BSB Office' },
    { flag: 'VN', country: 'Vietnam', note: 'Hanoi Office' },
    { flag: 'VN', country: 'Vietnam', note: 'Ho Chi Minh' },
  ],
  ctaTitle: 'Ready to ship with GE Lines?',
  ctaDescription: 'Get a freight quote or log in to your customer portal to track bookings and manage invoices.',
};

const DEFAULT_VISUAL: StorefrontVisualProfile = {
  industryTemplate: 'freight_forwarder',
  tone: 'corporate',
  mediaStrategy: 'industry_fallback',
  heroMediaUrl: '',
  heroMediaAlt: '',
  visualPattern: 'port_terminal',
  palette: 'ocean',
};

function toTitleCaseLabel(value: string): string {
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function buildHostFallbackBrand(host: string, tenantSlug: string): StorefrontBrand {
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    return { ...DEFAULT_BRAND, tenantSlug };
  }
  const hostLabel = host.split('.')[0] || tenantSlug || 'boox';
  const displayName = hostLabel === 'gelines' ? DEFAULT_BRAND.brandShortName : toTitleCaseLabel(hostLabel);
  const legalName = hostLabel === 'gelines' ? DEFAULT_BRAND.legalName : `${displayName} Logistics`;

  return {
    tenantSlug,
    brandName: legalName,
    brandShortName: displayName,
    legalName,
    logoUrl: undefined,
    portalThemeTemplate: 'freight_forwarder',
    tagline: 'Tenant-branded logistics workspace',
    primaryEmail: '',
    financeEmail: '',
    supportEmail: '',
    primaryPhone: '',
    secondaryPhone: '',
    addressLines: [],
    officeHours: [],
    footerBlurb: 'Tenant-branded digital logistics workspace.',
    portalLabel: 'Customer Portal',
  };
}

function buildHostFallbackHome(displayName: string) {
  return {
    heroBadge: 'Customer logistics workspace',
    heroTitle: displayName,
    heroAccent: 'Digital Operations',
    heroDescription: 'Preparing tenant storefront content...',
    stats: [],
    services: [],
    networkTitle: 'Preparing storefront',
    networkSubtitle: 'Loading tenant-specific branding and content',
    networkCountries: [],
    ctaTitle: `Welcome to ${displayName}`,
    ctaDescription: 'Tenant branding and public content are loading.',
  };
}

const StorefrontContext = createContext<StorefrontContextValue | null>(null);

const RESERVED_BOOX_APP_LABELS = new Set(['app', 'auth', 'share', 'cc', 'go', 'hub', 'www']);

function buildExperienceUrls(host: string, tenantSlug: string): {
  experienceMode: StorefrontExperienceMode;
  storefrontBaseUrl: string | null;
  portalBaseUrl: string | null;
  loginUrl: string;
  portalHomeUrl: string;
} {
  return buildExperienceUrlsWithPortal(host, tenantSlug, true);
}

function buildExperienceUrlsWithPortal(host: string, tenantSlug: string, portalLoginEnabled: boolean): {
  experienceMode: StorefrontExperienceMode;
  storefrontBaseUrl: string | null;
  portalBaseUrl: string | null;
  loginUrl: string;
  portalHomeUrl: string;
} {
  const normalizedHost = host.toLowerCase();
  const firstLabel = normalizedHost.split('.')[0] || tenantSlug;
  const portalLabel = firstLabel || tenantSlug;

  if (normalizedHost.endsWith('.boox.app') && !RESERVED_BOOX_APP_LABELS.has(firstLabel)) {
    const storefrontBaseUrl = `https://${portalLabel}.boox.network`;
    if (!portalLoginEnabled) {
      return {
        experienceMode: 'storefront',
        storefrontBaseUrl,
        portalBaseUrl: null,
        loginUrl: '/',
        portalHomeUrl: storefrontBaseUrl,
      };
    }
    const portalBaseUrl = `https://${portalLabel}.boox.app`;
    return {
      experienceMode: 'portal',
      storefrontBaseUrl,
      portalBaseUrl,
      loginUrl: `${portalBaseUrl}/login`,
      portalHomeUrl: `${portalBaseUrl}/portal`,
    };
  }

  if (normalizedHost.endsWith('.boox.network')) {
    const storefrontBaseUrl = `https://${portalLabel}.boox.network`;
    const portalBaseUrl = portalLoginEnabled ? `https://${portalLabel}.boox.app` : null;
    return {
      experienceMode: 'storefront',
      storefrontBaseUrl,
      portalBaseUrl,
      loginUrl: portalBaseUrl ? `${portalBaseUrl}/login` : '/',
      portalHomeUrl: portalBaseUrl ? `${portalBaseUrl}/portal` : storefrontBaseUrl,
    };
  }

  return {
    experienceMode: 'storefront',
    storefrontBaseUrl: null,
    portalBaseUrl: null,
    loginUrl: '/login',
    portalHomeUrl: '/portal',
  };
}

function buildFallback(host: string): StorefrontContextValue {
  const tenantSlug = host.includes('localhost') || host.includes('127.0.0.1')
    ? 'ge-lines'
    : (host.split('.')[0] === 'gelines' ? 'ge-lines' : host.split('.')[0] || 'ge-lines');
  const experience = buildExperienceUrls(host, tenantSlug);
  const brand = buildHostFallbackBrand(host, tenantSlug);
  const home = buildHostFallbackHome(brand.brandShortName);

  return {
    isLoading: false,
    host,
    tenantSlug,
    siteKey: 'default',
    ...experience,
    tokenKey: `boox_storefront:${tenantSlug}:default:token`,
    legacyTokenKeys: tenantSlug === 'ge-lines' ? ['gelines_token'] : [],
    brand,
    visual: DEFAULT_VISUAL,
    home,
    pages: [],
    routes: [
      { routePath: '/', routeType: 'page', targetKey: 'home', authMode: 'public', configJson: {} },
      { routePath: '/track', routeType: 'module', targetKey: 'tracking', authMode: 'public', configJson: {} },
      { routePath: '/book', routeType: 'page', targetKey: 'booking', authMode: 'public', configJson: {} },
      { routePath: '/rfq', routeType: 'page', targetKey: 'rfq', authMode: 'public', configJson: {} },
      { routePath: '/inquiry', routeType: 'page', targetKey: 'inquiry', authMode: 'public', configJson: {} },
      { routePath: '/contact', routeType: 'page', targetKey: 'contact', authMode: 'public', configJson: {} },
      { routePath: '/login', routeType: 'page', targetKey: 'login', authMode: 'public', configJson: {} },
    ],
    forms: [],
    modules: [{ moduleKey: 'tracking', moduleType: 'tracking', configJson: { public: true } }],
    featureFlags: { tracking: true, portalLogin: true, bookings: true },
    raw: null,
  };
}

export function StorefrontProvider({ children }: { children: ReactNode }) {
  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : 'localhost';
  const [value, setValue] = useState<StorefrontContextValue>(() => ({
    ...buildFallback(host),
    isLoading: true,
  }));

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/storefront/bootstrap?host=${encodeURIComponent(host)}`, {
          signal: AbortSignal.timeout(8000),
        });
        if (!res.ok) throw new Error('bootstrap failed');
        const envelope = await res.json() as {
          ok: boolean;
          data?: {
            tenant?: { slug?: string };
            site?: { siteKey?: string; featureFlags?: Record<string, unknown> };
            publishedVersion?: { content?: Record<string, unknown> };
            routes?: Array<Record<string, unknown>>;
            forms?: Array<Record<string, unknown>>;
            modules?: Array<Record<string, unknown>>;
          };
        };
        if (!envelope.ok || !envelope.data) throw new Error('bootstrap failed');

        const tenantSlug = String(envelope.data.tenant?.slug ?? 'ge-lines');
        const siteKey = String(envelope.data.site?.siteKey ?? 'default');
        const content = envelope.data.publishedVersion?.content ?? {};
        const site = (content.site as Partial<StorefrontBrand> | undefined) ?? {};
        const siteRecord = (content.site as Record<string, unknown> | undefined) ?? {};
        const visualRecord = (siteRecord.visual && typeof siteRecord.visual === 'object'
          ? siteRecord.visual
          : {}) as Partial<StorefrontVisualProfile>;
        const home = (content.home as Record<string, unknown> | undefined) ?? DEFAULT_HOME;
        const rawPages = Array.isArray(content.pages) ? content.pages : [];
        const rawRoutes = Array.isArray(envelope.data.routes) ? envelope.data.routes : [];
        const rawForms = Array.isArray(envelope.data.forms) ? envelope.data.forms : [];
        const rawModules = Array.isArray(envelope.data.modules) ? envelope.data.modules : [];
        const featureFlags = Object.fromEntries(
          Object.entries(envelope.data.site?.featureFlags ?? {}).map(([key, flag]) => [key, Boolean(flag)]),
        );
        const experience = buildExperienceUrlsWithPortal(host, tenantSlug, Boolean(featureFlags.portalLogin));

        if (cancelled) return;
        setValue({
          isLoading: false,
          host,
          tenantSlug,
          siteKey,
          ...experience,
          tokenKey: `boox_storefront:${tenantSlug}:${siteKey}:token`,
          legacyTokenKeys: tenantSlug === 'ge-lines' ? ['gelines_token'] : [],
          brand: {
            tenantSlug,
            brandName: site.brandName ?? DEFAULT_BRAND.brandName,
            brandShortName: site.brandShortName ?? site.brandName ?? DEFAULT_BRAND.brandShortName,
            legalName: site.legalName ?? site.brandName ?? DEFAULT_BRAND.legalName,
            logoUrl: typeof site.logoUrl === 'string'
              ? site.logoUrl
              : typeof siteRecord.logo_url === 'string'
                ? siteRecord.logo_url
                : DEFAULT_BRAND.logoUrl,
            portalThemeTemplate: typeof site.portalThemeTemplate === 'string'
              ? site.portalThemeTemplate
              : typeof siteRecord.portal_theme_template === 'string'
                ? siteRecord.portal_theme_template
                : DEFAULT_BRAND.portalThemeTemplate,
            tagline: site.tagline ?? DEFAULT_BRAND.tagline,
            primaryEmail: site.primaryEmail ?? DEFAULT_BRAND.primaryEmail,
            financeEmail: site.financeEmail ?? DEFAULT_BRAND.financeEmail,
            supportEmail: site.supportEmail ?? DEFAULT_BRAND.supportEmail,
            primaryPhone: site.primaryPhone ?? DEFAULT_BRAND.primaryPhone,
            secondaryPhone: site.secondaryPhone ?? DEFAULT_BRAND.secondaryPhone,
            addressLines: Array.isArray(site.addressLines) ? site.addressLines.filter((v): v is string => typeof v === 'string') : DEFAULT_BRAND.addressLines,
            officeHours: Array.isArray(site.officeHours) ? site.officeHours.filter((v): v is string => typeof v === 'string') : DEFAULT_BRAND.officeHours,
            footerBlurb: site.footerBlurb ?? DEFAULT_BRAND.footerBlurb,
            portalLabel: site.portalLabel ?? DEFAULT_BRAND.portalLabel,
          },
          visual: {
            ...DEFAULT_VISUAL,
            ...visualRecord,
            heroMediaUrl: typeof visualRecord.heroMediaUrl === 'string' ? visualRecord.heroMediaUrl : '',
            heroMediaAlt: typeof visualRecord.heroMediaAlt === 'string' ? visualRecord.heroMediaAlt : '',
          },
          home,
          pages: rawPages.map((page) => ({
            pageKey: String(page.pageKey ?? page.path ?? ''),
            path: page.path ? String(page.path) : undefined,
            title: page.title ? String(page.title) : undefined,
            intro: page.intro ? String(page.intro) : undefined,
            sections: Array.isArray(page.sections) ? page.sections : [],
          })),
          routes: rawRoutes.map((route) => ({
            routePath: String(route.routePath ?? ''),
            routeType: String(route.routeType ?? 'page') as StorefrontRoute['routeType'],
            targetKey: String(route.targetKey ?? ''),
            authMode: String(route.authMode ?? 'public') as StorefrontRoute['authMode'],
            configJson: (route.configJson ?? {}) as Record<string, unknown>,
          })),
          forms: rawForms.map((form) => ({
            formKey: String(form.formKey ?? ''),
            title: String(form.title ?? ''),
            description: form.description ? String(form.description) : null,
            entityType: String(form.entityType ?? ''),
            schemaJson: (form.schemaJson ?? {}) as StorefrontFormDefinition['schemaJson'],
            submitConfig: (form.submitConfig ?? {}) as Record<string, unknown>,
            notificationCfg: (form.notificationCfg ?? {}) as Record<string, unknown>,
          })),
          modules: rawModules.map((module) => ({
            moduleKey: String(module.moduleKey ?? ''),
            moduleType: String(module.moduleType ?? ''),
            configJson: (module.configJson ?? {}) as Record<string, unknown>,
          })),
          featureFlags,
          raw: envelope.data as unknown as Record<string, unknown>,
        });
      } catch {
        if (!cancelled) {
          setValue(buildFallback(host));
        }
      }
    };

    void run();
    return () => { cancelled = true; };
  }, [host]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = `${value.brand.brandShortName} — ${value.brand.portalLabel}`;
    }
  }, [value.brand.brandShortName, value.brand.portalLabel]);

  const memoValue = useMemo(() => value, [value]);
  return <StorefrontContext.Provider value={memoValue}>{children}</StorefrontContext.Provider>;
}

export function useStorefront(): StorefrontContextValue {
  const ctx = useContext(StorefrontContext);
  if (!ctx) throw new Error('useStorefront must be used inside <StorefrontProvider>');
  return ctx;
}
